import type { ScheduleType } from "../db/schema";
import type { ScheduleConfig } from "./types";

/**
 * Parse and validate the schedule_value JSON string from the DB into a typed ScheduleConfig.
 * Throws descriptive errors on invalid config.
 */
export function parseScheduleConfig(
  scheduleType: ScheduleType,
  scheduleValue: string,
): ScheduleConfig {
  switch (scheduleType) {
    case "daily":
      return { type: "daily" };

    case "specific_days": {
      const parsed: unknown = JSON.parse(scheduleValue);
      assertObject(parsed, "specific_days");

      const days = parsed.days;
      if (!Array.isArray(days) || days.length === 0) {
        throw new Error("specific_days config must have a non-empty 'days' array");
      }

      for (const day of days) {
        if (typeof day !== "number" || !Number.isInteger(day) || day < 0 || day > 6) {
          throw new Error(`specific_days: each day must be an integer 0-6, got ${String(day)}`);
        }
      }

      const uniqueDays = [...new Set(days as number[])].sort((a, b) => a - b);
      return { type: "specific_days", days: uniqueDays };
    }

    case "x_per_week": {
      const parsed: unknown = JSON.parse(scheduleValue);
      assertObject(parsed, "x_per_week");

      const count = parsed.count;
      if (typeof count !== "number" || !Number.isInteger(count) || count < 1 || count > 7) {
        throw new Error(`x_per_week: count must be an integer 1-7, got ${String(count)}`);
      }

      return { type: "x_per_week", count };
    }

    case "x_per_month": {
      const parsed: unknown = JSON.parse(scheduleValue);
      assertObject(parsed, "x_per_month");

      const count = parsed.count;
      if (typeof count !== "number" || !Number.isInteger(count) || count < 1 || count > 31) {
        throw new Error(`x_per_month: count must be an integer 1-31, got ${String(count)}`);
      }

      return { type: "x_per_month", count };
    }

    case "every_n_days": {
      const parsed: unknown = JSON.parse(scheduleValue);
      assertObject(parsed, "every_n_days");

      const interval = parsed.interval;
      if (
        typeof interval !== "number" ||
        !Number.isInteger(interval) ||
        interval < 2 ||
        interval > 365
      ) {
        throw new Error(`every_n_days: interval must be an integer 2-365, got ${String(interval)}`);
      }

      return { type: "every_n_days", interval };
    }

    default: {
      const _exhaustive: never = scheduleType;
      throw new Error(`Unknown schedule type: ${String(_exhaustive)}`);
    }
  }
}

function assertObject(value: unknown, label: string): asserts value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} config must be a JSON object`);
  }
}
